import React, { useState, useEffect, useRef } from 'react';
import { apiGet, apiPut, apiPostForm, apiDelete, AuthError } from '../api';
import { useClient } from '../ClientContext';

/**
 * Brand Guidelines.
 *
 * What a client's advertising is allowed to look like, held in one place so
 * Agentic Studio opens already knowing it. Nothing on this page publishes
 * anything — these are inputs to creative, not creative.
 *
 * The palette, spacing and typography save together as one document. Files
 * save immediately on upload, because a half-uploaded logo waiting for someone
 * to press Save is a worse state than a logo that is simply there.
 */

const LOGO_VARIANTS = [
  { value: 'primary',  label: 'Primary' },
  { value: 'reversed', label: 'Reversed (for dark backgrounds)' },
  { value: 'mark',     label: 'Mark only' },
  { value: 'wordmark', label: 'Wordmark' },
];

const FONT_WEIGHTS = ['300', '400', '500', '600', '700', '800'];

// Sensible starting values rather than an empty form. A brand with no spacing
// rules configured still needs the studio to draw a safe area somewhere, and
// these are the values it would fall back to anyway — so showing them makes
// the default visible rather than hidden in the renderer.
const DEFAULT_SPACING = {
  base_unit: 8,
  safe_margin_pct: 5,
  corner_radius: 12,
  logo_clear_space_pct: 4,
};

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function Section({ title, description, children, action }) {
  return (
    <div className="card" style={{marginBottom:20, padding:0}}>
      <div style={{
        padding:'18px 22px', borderBottom:'1px solid var(--grey-light)',
        display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16,
      }}>
        <div>
          <div style={{fontSize:14, fontWeight:600, color:'var(--pine)'}}>{title}</div>
          {description && (
            <div style={{fontSize:12.5, color:'var(--grey)', marginTop:4, maxWidth:620}}>
              {description}
            </div>
          )}
        </div>
        {action}
      </div>
      <div style={{padding:22}}>{children}</div>
    </div>
  );
}

export default function BrandGuidelines() {
  const { metaAccountId: accountId, selected, isAll } = useClient();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [palette, setPalette] = useState([]);
  const [typography, setTypography] = useState({
    heading: { family: '', weight: '700' },
    body: { family: '', weight: '400' },
  });
  const [spacing, setSpacing] = useState(DEFAULT_SPACING);
  const [toneNotes, setToneNotes] = useState('');

  const [logos, setLogos] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Which variant the next uploaded file is recorded as. Chosen before the file
  // picker opens rather than after, because the browser dialog is modal and a
  // question asked on the far side of it is a question asked too late.
  const [logoVariant, setLogoVariant] = useState('primary');
  const [fontWeight, setFontWeight] = useState('400');

  const logoInputRef = useRef(null);
  const fontInputRef = useRef(null);
  const pendingKind = useRef(null);

  const load = () => {
    if (!accountId) { setLoading(false); return; }
    apiGet(`/brand/${accountId}`)
      .then(data => {
        const g = data.guidelines || {};
        setPalette(g.palette || []);
        setTypography({
          heading: { family: '', weight: '700', ...(g.typography?.heading || {}) },
          body: { family: '', weight: '400', ...(g.typography?.body || {}) },
        });
        // Merge over the defaults rather than replacing them, so a brand saved
        // before a field existed does not come back with that field missing.
        setSpacing({ ...DEFAULT_SPACING, ...(g.spacing || {}) });
        setToneNotes(g.tone_notes || '');
        setLogos(data.logos || []);
        setFonts(data.fonts || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof AuthError ? err.message : err.message || 'Could not load brand guidelines.');
        setLoading(false);
      });
  };

  useEffect(() => {
    setError(null);
    setNotice(null);
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  // ── Palette ────────────────────────────────────────────────────────────────

  const addColour = () => setPalette(p => [...p, { name: '', hex: '#000000', role: '' }]);
  const updateColour = (i, patch) =>
    setPalette(p => p.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const removeColour = (i) => setPalette(p => p.filter((_, idx) => idx !== i));

  // The server rejects a malformed hex outright, so the same check runs here —
  // otherwise the first a user hears of a typo is the whole save failing.
  const badColours = palette.filter(c => !HEX.test((c.hex || '').trim()));

  const saveGuidelines = async () => {
    if (badColours.length) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await apiPut(`/brand/${accountId}`, {
        palette: palette.map(c => ({
          name: (c.name || '').trim(),
          hex: (c.hex || '').trim().toLowerCase(),
          role: (c.role || '').trim(),
        })),
        typography,
        spacing,
        tone_notes: toneNotes,
      });
      setNotice('Brand guidelines saved. Agentic Studio will use these on the next creative you build.');
    } catch (err) {
      setError(err.message || 'Could not save brand guidelines.');
    }
    setSaving(false);
  };

  // ── Files ──────────────────────────────────────────────────────────────────

  const startUpload = (kind) => {
    pendingKind.current = kind;
    (kind === 'LOGO' ? logoInputRef : fontInputRef).current?.click();
  };

  const handleFile = async (file) => {
    if (!file) return;
    const kind = pendingKind.current;
    setError(null);
    setNotice(null);
    setUploading(kind);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('kind', kind);
    // The filename without its extension is the obvious default label, and it
    // is usually what the designer already called it.
    formData.append('label', file.name.replace(/\.[^.]+$/, ''));
    formData.append('variant', kind === 'LOGO' ? logoVariant : fontWeight);

    try {
      await apiPostForm(`/brand/${accountId}/asset`, formData);
      load();
      setNotice(kind === 'LOGO' ? 'Logo added.' : 'Font added.');
    } catch (err) {
      setError(err.message || 'Upload failed.');
    }
    setUploading(null);
  };

  const removeAsset = async (asset) => {
    setDeleting(asset.brand_asset_id);
    setError(null);
    try {
      await apiDelete(`/brand/${accountId}/asset/${asset.brand_asset_id}`);
      load();
    } catch (err) {
      setError(err.message || 'Could not remove this file.');
    }
    setDeleting(null);
  };


  // ── Render ─────────────────────────────────────────────────────────────────

  if (isAll) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Brand Guidelines</h1>
        <p className="page-subtitle">Brand guidelines are held per client</p>
      </div>
      <div className="card">
        <div className="empty-state">
          <h3>Select a client</h3>
          <p>Choose a client from the switcher above to view or edit their brand.</p>
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh'}}>
      <div style={{color:'var(--grey)',fontSize:14}}>Loading brand guidelines...</div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Brand Guidelines</h1>
        <p className="page-subtitle">
          {selected ? `${selected.name} — ` : ''}
          colours, type, spacing and logo files. Agentic Studio reads these when it opens.
        </p>
      </div>

      {error && (
        <div className="card" style={{marginBottom:20,padding:'12px 20px',borderLeft:'3px solid var(--red)'}}>
          <div style={{fontSize:13,color:'var(--red)',fontWeight:600}}>{error}</div>
        </div>
      )}
      {notice && (
        <div className="card" style={{marginBottom:20,padding:'12px 20px',borderLeft:'3px solid var(--green)'}}>
          <div style={{fontSize:13,color:'var(--green)',fontWeight:600}}>{notice}</div>
        </div>
      )}

      <input
        ref={logoInputRef} type="file" style={{display:'none'}}
        accept="image/png,image/jpeg,image/gif,image/webp"
        onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }}
      />
      <input
        ref={fontInputRef} type="file" style={{display:'none'}}
        accept=".woff2,.woff,.ttf,.otf"
        onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }}
      />

      {/* ── Logos ── */}
      <Section
        title="Logos"
        description={
          'Upload each variant separately. A reversed logo is not the same file as a '
          + 'primary one, and the studio picks between them by the background it is '
          + 'placed on. PNG, JPG, GIF or WebP.'
        }
        action={
          <div style={{display:'flex', gap:8, alignItems:'center'}}>
            <select
              className="form-select"
              value={logoVariant}
              onChange={e => setLogoVariant(e.target.value)}
              style={{width:200, padding:'6px 10px', fontSize:12}}
              aria-label="Variant for the next logo uploaded"
            >
              {LOGO_VARIANTS.map(v => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
            <button
              className="btn btn-secondary"
              style={{padding:'6px 14px', fontSize:12, whiteSpace:'nowrap'}}
              onClick={() => startUpload('LOGO')}
              disabled={uploading === 'LOGO'}
            >
              {uploading === 'LOGO' ? 'Uploading...' : 'Add logo'}
            </button>
          </div>
        }
      >
        {logos.length === 0 ? (
          <div style={{fontSize:13, color:'var(--grey)'}}>
            No logos yet. Without one, the studio can still build creative — it just
            cannot place a logo on it.
          </div>
        ) : (
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:16}}>
            {logos.map(logo => (
              <div key={logo.brand_asset_id} style={{
                border:'1px solid var(--grey-light)', borderRadius:'var(--radius-md)',
                overflow:'hidden',
              }}>
                {/* Chequerboard, because most logos are transparent PNGs and a
                    white tile makes a white logo look like a failed load. */}
                <div style={{
                  height:120, display:'flex', alignItems:'center', justifyContent:'center',
                  padding:12,
                  backgroundColor:'var(--paper-dark)',
                  backgroundImage:
                    'linear-gradient(45deg, rgba(0,0,0,0.04) 25%, transparent 25%),' +
                    'linear-gradient(-45deg, rgba(0,0,0,0.04) 25%, transparent 25%),' +
                    'linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.04) 75%),' +
                    'linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.04) 75%)',
                  backgroundSize:'14px 14px',
                  backgroundPosition:'0 0, 0 7px, 7px -7px, -7px 0',
                }}>
                  {logo.url ? (
                    <img src={logo.url} alt={logo.label}
                         style={{maxWidth:'100%', maxHeight:'100%'}} />
                  ) : (
                    <span style={{fontSize:11.5, color:'var(--red)'}}>File unavailable</span>
                  )}
                </div>
                <div style={{padding:'10px 12px'}}>
                  <div style={{fontSize:12.5, fontWeight:600, color:'var(--pine)',
                               overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                    {logo.label}
                  </div>
                  <div style={{fontSize:11, color:'var(--grey)', marginTop:2}}>
                    {logo.variant || 'primary'}{logo.format ? ` · ${logo.format}` : ''}
                  </div>
                  <button
                    onClick={() => removeAsset(logo)}
                    disabled={deleting === logo.brand_asset_id}
                    style={{background:'none', border:'none', padding:0, marginTop:8,
                            color:'var(--red)', fontSize:11.5, cursor:'pointer'}}
                  >
                    {deleting === logo.brand_asset_id ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── Palette ── */}
      <Section
        title="Colour palette"
        description={
          'Order matters — this is the order swatches appear in the studio, so put the '
          + 'colours used most first. Role is free text and is passed to the image model '
          + 'as context, so "background", "accent" or "never on white" are all useful.'
        }
        action={
          <button className="btn btn-secondary"
                  style={{padding:'6px 14px', fontSize:12, whiteSpace:'nowrap'}}
                  onClick={addColour}>
            Add colour
          </button>
        }
      >
        {palette.length === 0 ? (
          <div style={{fontSize:13, color:'var(--grey)'}}>
            No colours yet. Add the brand’s core colours so the studio can offer them
            instead of a generic picker.
          </div>
        ) : (
          <div style={{display:'flex', flexDirection:'column', gap:10}}>
            {palette.map((colour, i) => {
              const invalid = !HEX.test((colour.hex || '').trim());
              return (
                <div key={i} style={{
                  display:'grid',
                  gridTemplateColumns:'44px 1.2fr 130px 1.2fr 32px',
                  gap:10, alignItems:'center',
                }}>
                  <input
                    type="color"
                    value={HEX.test(colour.hex || '') ? colour.hex : '#000000'}
                    onChange={e => updateColour(i, { hex: e.target.value })}
                    style={{width:44, height:36, padding:2, border:'1px solid var(--grey-light)',
                            borderRadius:'var(--radius-sm)', background:'none', cursor:'pointer'}}
                  />
                  <input
                    className="form-input"
                    placeholder="Name, e.g. Pine"
                    value={colour.name || ''}
                    maxLength={60}
                    onChange={e => updateColour(i, { name: e.target.value })}
                  />
                  <input
                    className="form-input mono"
                    placeholder="#0F2E24"
                    value={colour.hex || ''}
                    onChange={e => updateColour(i, { hex: e.target.value })}
                    style={invalid ? {borderColor:'var(--red)'} : undefined}
                  />
                  <input
                    className="form-input"
                    placeholder="Role, e.g. background"
                    value={colour.role || ''}
                    maxLength={40}
                    onChange={e => updateColour(i, { role: e.target.value })}
                  />
                  <button
                    onClick={() => removeColour(i)}
                    aria-label={`Remove ${colour.name || 'colour'}`}
                    style={{background:'none', border:'none', cursor:'pointer',
                            color:'var(--grey)', fontSize:18, lineHeight:1}}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            {badColours.length > 0 && (
              <div style={{fontSize:12, color:'var(--red)', fontWeight:600, marginTop:2}}>
                {badColours.length === 1 ? 'One colour is' : `${badColours.length} colours are`}
                {' '}not a valid hex value. Use #RGB or #RRGGBB.
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ── Typography ── */}
      <Section
        title="Typography"
        description={
          'Upload the brand’s font files so text added in the studio is set in the real '
          + 'typeface rather than an approximation. Where no file is uploaded, the family '
          + 'name is used and the studio falls back to a system font if it is not available.'
        }
        action={
          <div style={{display:'flex', gap:8, alignItems:'center'}}>
            <select
              className="form-select"
              value={fontWeight}
              onChange={e => setFontWeight(e.target.value)}
              style={{width:100, padding:'6px 10px', fontSize:12}}
              aria-label="Weight for the next font file uploaded"
            >
              {FONT_WEIGHTS.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
            <button
              className="btn btn-secondary"
              style={{padding:'6px 14px', fontSize:12, whiteSpace:'nowrap'}}
              onClick={() => startUpload('FONT')}
              disabled={uploading === 'FONT'}
            >
              {uploading === 'FONT' ? 'Uploading...' : 'Add font file'}
            </button>
          </div>
        }
      >
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20}}>
          {['heading', 'body'].map(role => (
            <div key={role}>
              <label className="form-label" style={{textTransform:'capitalize'}}>{role}</label>
              <div style={{display:'grid', gridTemplateColumns:'1fr 100px', gap:8}}>
                <input
                  className="form-input"
                  placeholder={role === 'heading' ? 'e.g. Sora' : 'e.g. Instrument Sans'}
                  value={typography[role]?.family || ''}
                  onChange={e => setTypography(t => ({
                    ...t, [role]: { ...t[role], family: e.target.value }
                  }))}
                />
                <select
                  className="form-select"
                  value={typography[role]?.weight || '400'}
                  onChange={e => setTypography(t => ({
                    ...t, [role]: { ...t[role], weight: e.target.value }
                  }))}
                >
                  {FONT_WEIGHTS.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>

        {fonts.length === 0 ? (
          <div style={{fontSize:13, color:'var(--grey)'}}>
            No font files uploaded. WOFF2, WOFF, TTF or OTF.
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>File</th><th>Weight</th><th>Size</th><th></th></tr>
            </thead>
            <tbody>
              {fonts.map(font => (
                <tr key={font.brand_asset_id}>
                  <td className="mono" style={{fontSize:12.5}}>{font.label}</td>
                  <td style={{fontSize:12, color:'var(--grey)'}}>{font.variant || '—'}</td>
                  <td style={{fontSize:12, color:'var(--grey)'}}>
                    {font.file_size_bytes
                      ? `${Math.round(font.file_size_bytes / 1024)} KB`
                      : '—'}
                  </td>
                  <td>
                    <button
                      onClick={() => removeAsset(font)}
                      disabled={deleting === font.brand_asset_id}
                      style={{background:'none', border:'none', padding:0,
                              color:'var(--red)', fontSize:11.5, cursor:'pointer'}}
                    >
                      {deleting === font.brand_asset_id ? 'Removing...' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* ── Spacing ── */}
      <Section
        title="Spacing"
        description={
          'The studio draws a safe area from these and warns when something crosses it. '
          + 'Safe margin is the one that matters most: text placed outside it is what gets '
          + 'cropped on a phone.'
        }
      >
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:16}}>
          {[
            { key:'base_unit', label:'Base unit (px)', hint:'Grid step for nudging and alignment' },
            { key:'safe_margin_pct', label:'Safe margin (%)', hint:'Kept clear of the canvas edge' },
            { key:'corner_radius', label:'Corner radius (px)', hint:'Applied to boxes and buttons' },
            { key:'logo_clear_space_pct', label:'Logo clear space (%)', hint:'Kept clear around the logo' },
          ].map(field => (
            <div key={field.key} className="form-group" style={{marginBottom:0}}>
              <label className="form-label">{field.label}</label>
              <input
                className="form-input"
                type="number"
                min={0}
                max={100}
                value={spacing[field.key] ?? ''}
                onChange={e => setSpacing(s => ({
                  ...s,
                  [field.key]: e.target.value === '' ? '' : Number(e.target.value),
                }))}
              />
              <div style={{fontSize:11.5, color:'var(--grey)', marginTop:5}}>{field.hint}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Tone ── */}
      <Section
        title="Tone and restrictions"
        description={
          'Passed to the image model as context whenever a creative is generated or edited. '
          + 'Restrictions are worth stating plainly here — "never place the logo on a '
          + 'photograph", "no stock imagery of people" — because the model only knows what '
          + 'this says.'
        }
      >
        <textarea
          className="form-input"
          rows={5}
          maxLength={2000}
          value={toneNotes}
          onChange={e => setToneNotes(e.target.value)}
          placeholder="How this brand speaks and looks, and anything it never does."
          style={{resize:'vertical', fontFamily:'inherit'}}
        />
        <div style={{fontSize:11.5, color:'var(--grey)', marginTop:6, textAlign:'right'}}>
          {toneNotes.length} / 2000
        </div>
      </Section>

      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:40}}>
        <button
          className="btn btn-approve"
          onClick={saveGuidelines}
          disabled={saving || badColours.length > 0}
        >
          {saving ? 'Saving...' : 'Save brand guidelines'}
        </button>
        <span style={{fontSize:12, color:'var(--grey)'}}>
          Logos and fonts save as soon as they are uploaded. This saves the colours,
          type, spacing and tone.
        </span>
      </div>
    </div>
  );
}
